import { PayloadAction } from "@reduxjs/toolkit";
import { Task } from "redux-saga";
import {
  call,
  cancel,
  fork,
  put,
  select,
  StrictEffect,
  take
} from "redux-saga/effects";

import { Entity } from "@/jenesys-types/data/common";
import { EntityListResponse } from "@/jenesys-types/data/crud";
import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { ListSearchCriteria } from "@/jenesys-types/list";

import { listData } from "@/store/data/data.saga";
import { ids } from "@/util/data";
import { performanceMonitor } from "@/utils/performance";

import { loadRelatedData } from "./crudUtils.saga";
import {
  BaseListPayload,
  filterByStatus,
  List,
  listLoadFailure,
  listLoadSuccess,
  loadList,
  loadListWithoutClearing,
  loadNextListPage,
  loadSpecificListPage,
  refreshList,
  searchList,
  sortList,
  updateQueryParams
} from "./list";

import { selectListState } from ".";

const DEFAULT_PAGE_SIZE = 20;

/**
 * Checks if search criteria contains any valid filters
 */
const containsValidSearchCriteria = (
  criteria?: ListSearchCriteria
): boolean => {
  const flatCriteria = Object.values(criteria || {}).flat();
  return flatCriteria.length > 0;
};

/**
 * Determines if either search or filter criteria contains valid filters
 * Used to decide between simple list loading and search-based loading
 */
const isSearchOrFilterCriteriaValid = (
  searchCriteria?: ListSearchCriteria,
  filterCriteria?: ListSearchCriteria
): boolean =>
  containsValidSearchCriteria(searchCriteria) ||
  containsValidSearchCriteria(filterCriteria);

/**
 * Calculates the total count from all status counts
 * Used when no specific status filter is active
 */
const calculateTotalFromStatusCounts = (
  statusCounts?: Record<string, number>
): number => {
  if (!statusCounts) return 0;
  return Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
};

// const getClientSideSearchCriteria = (
//   criteria?: ListSearchCriteria
// ): Array<ClientSideSearchCriterion> => {
//   const flatCriteria = Object.entries(criteria || {}).flatMap(
//     ([fieldName, fieldCriteria]) =>
//       Array.isArray(fieldCriteria)
//         ? fieldCriteria.map((fieldCriterion) => ({ fieldName, fieldCriterion }))
//         : [{ fieldName, fieldCriterion: fieldCriteria }]
//   );
//   return flatCriteria
//     .filter(
//       (fieldCriteria) =>
//         fieldCriteria.fieldCriterion.comparator ===
//         ListSearchComparator.Contains
//     )
//     .map((fieldCriteria) => ({
//       fieldName: fieldCriteria.fieldName,
//       searchQuery: `${fieldCriteria.fieldCriterion.value}`
//     }));
// };

// Map of search operators to their API representation
const OPERATOR_MAP = {
  contains: "contains",
  equal: "=",
  greaterThan: ">",
  greaterThanOrEqual: ">=",
  lessThan: "<",
  lessThanOrEqual: "<=",
  notEqual: "!="
};

/**
 * Converts ListSearchCriteria to an array of search query parameters
 * in the format field:operator:value for the API
 */
const createSearchParams = (criteria?: ListSearchCriteria): Array<string> => {
  if (!criteria) {
    return [];
  }

  const searchParams: Array<string> = [];

  // Flatten the criteria into an array of { field, criterion } pairs
  Object.entries(criteria).forEach(([field, criterion]) => {
    if (Array.isArray(criterion)) {
      criterion.forEach((c) => {
        const operator = OPERATOR_MAP[c.comparator] || "contains";
        searchParams.push(`${field}:${operator}:${c.value}`);
      });
    } else {
      const operator = OPERATOR_MAP[criterion.comparator] || "contains";
      searchParams.push(`${field}:${operator}:${criterion.value}`);
    }
  });

  return searchParams;
};

/**
 * Loads list data without complex search criteria
 * Handles status filtering and pagination for basic list views
 */
export function* loadSimpleListData(
  dataType: DataType,
  listId: string,
  listState: List
): Generator<StrictEffect, EntityListResponse<Entity> | undefined, any> {
  const metricName = "Saga.loadSimpleListData";
  const initialContext = {
    sagaInstance: `${dataType}-${listId}`,
    dataType,
    listId,
    actionName: listState.actionName,
    pathParams: listState.pathParams,
    queryParams: listState.queryParams, // Initial query params
    currentPageIndex: listState.currentPageIndex,
    limit: listState.limit,
    lastChosenStatus: listState.lastChosenStatus
  };
  const metricId = performanceMonitor.start(metricName, initialContext);
  try {
    const previousPageState =
      listState.currentPageIndex > 0
        ? listState.pages[listState.currentPageIndex - 1]
        : undefined;
    const newPageIndex = previousPageState ? listState.currentPageIndex : 0;

    // Include all query params along with pagination
    const queryParamsForApi: Record<string, any> = {
      limit: DEFAULT_PAGE_SIZE,
      offset: `${listState.limit * listState.currentPageIndex}`,
      ...listState.queryParams
    };

    const sortParams =
      listState.baselineSorts?.filter(
        ({ sortBy }) => sortBy !== listState.sortCriteria?.sortBy
      ) ?? [];
    if (listState.sortCriteria?.sortBy) {
      if (listState.sortCriteria.sortBy === "category_code") {
        sortParams.unshift(
          {
            sortBy: "extracted_category_code",
            sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
          },
          {
            sortBy: "extracted_category_name",
            sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
          }
        );
      } else {
        sortParams.unshift({
          sortBy: listState.sortCriteria.sortBy,
          sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
        });
      }
    }
    if (sortParams.length > 0) {
      queryParamsForApi.order_by = sortParams.map(
        ({ sortBy, sortOrder }) => `${sortBy}:${sortOrder.toLowerCase()}`
      );
    }

    // Add status filter to query params if active
    if (listState.lastChosenStatus) {
      if (dataType === DataType.BankStatement) {
        queryParamsForApi.reconciliation_status = listState.lastChosenStatus;
      } else {
        queryParamsForApi.document_status = listState.lastChosenStatus;
      }
    } else if (
      listState.statusesFilter &&
      listState.statusesFilter.length > 0
    ) {
      if (dataType === DataType.BankStatement) {
        queryParamsForApi.reconciliation_status = listState.statusesFilter;
      } else {
        queryParamsForApi.document_status = listState.statusesFilter;
      }
    }

    const response: EntityListResponse<Entity> = yield call(
      listData,
      dataType,
      queryParamsForApi, // Use the specifically constructed params for API call
      listState.actionName,
      listState.pathParams
    );

    const relatedData = yield call(
      loadRelatedData,
      dataType,
      response.items,
      listState.relationships
    );

    // This ensures pagination displays proper counts for filtered views
    let totalCount;
    if (
      listState.lastChosenStatus &&
      response.status_counts &&
      response.status_counts[listState.lastChosenStatus]
    ) {
      // When filtering by status, use the count for that specific status
      totalCount = response.status_counts[listState.lastChosenStatus];
    } else if (response.total) {
      // If the API returns a total directly, use that
      totalCount = response.total;
    } else {
      // Otherwise calculate from all status counts
      totalCount = calculateTotalFromStatusCounts(response.status_counts);
    }

    yield put(
      listLoadSuccess(
        dataType,
        listId,
        ids(response.items),
        newPageIndex,
        relatedData,
        totalCount, // Use the status-specific count for pagination
        response.limit,
        response.offset,
        response.status_counts
      )
    );

    return response;
  } catch (error) {
    yield put(listLoadFailure(dataType, listId, extractError(error)));
    return undefined; // Ensure a return path for catch
  } finally {
    // Stop the metric with error context if applicable
    performanceMonitor.stop(metricId, { completed: true });
  }
}

/**
 * Loads list data with search/filter criteria
 * Handles more complex filtering including status filtering
 */
export function* loadListDataWithServerSearch(
  dataType: DataType,
  listId: string,
  listState: List
): Generator<StrictEffect, EntityListResponse<Entity> | undefined, any> {
  const metricName = "Saga.loadListDataWithServerSearch";
  const initialContext = {
    sagaInstance: `${dataType}-${listId}`,
    dataType,
    listId,
    searchCriteria: listState.searchCriteria,
    filterCriteria: listState.filterCriteria,
    sortCriteria: listState.sortCriteria,
    pathParams: listState.pathParams,
    useSimpleSearch: listState.useSimpleSearch,
    offset: listState.offset,
    limit: listState.limit,
    lastChosenStatus: listState.lastChosenStatus
  };
  const metricId = performanceMonitor.start(metricName, initialContext);
  try {
    const newPageIndex = listState.currentPageIndex || 0;

    // Base query params - DO NOT include search params here yet
    const queryParams: Record<string, any> = {};
    // TODO: Remove this when search endpoints are updated to use criterion rather than value
    if (listState.useSimpleSearch && listState.searchCriteria) {
      // For simple search, just use the first search criterion's value
      const firstSearchCriterion = Object.values(listState.searchCriteria)[0];
      if (firstSearchCriterion) {
        const searchValue = Array.isArray(firstSearchCriterion)
          ? firstSearchCriterion[0].value
          : firstSearchCriterion.value;
        queryParams.search = searchValue;
        queryParams.offset = listState.offset;
        queryParams.limit = listState.limit;
      }
    } else {
      // Create search params from both search and filter criteria
      const searchParams = [
        ...createSearchParams(listState.searchCriteria),
        ...createSearchParams(listState.filterCriteria)
      ];

      // Add search parameters as an array
      if (searchParams.length > 0) {
        queryParams.search = searchParams;
        queryParams.offset = listState.offset;
        queryParams.limit = listState.limit;
      }
    }

    const sortParams =
      listState.baselineSorts?.filter(
        ({ sortBy }) => sortBy !== listState.sortCriteria?.sortBy
      ) ?? [];
    if (listState.sortCriteria?.sortBy) {
      if (listState.sortCriteria.sortBy === "category_code") {
        sortParams.unshift(
          {
            sortBy: "extracted_category_code",
            sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
          },
          {
            sortBy: "extracted_category_name",
            sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
          }
        );
      } else {
        sortParams.unshift({
          sortBy: listState.sortCriteria.sortBy,
          sortOrder: listState.sortCriteria.sortOrder ?? "ASC"
        });
      }
    }
    if (sortParams.length > 0) {
      queryParams.order_by = sortParams.map(
        ({ sortBy, sortOrder }) => `${sortBy}:${sortOrder.toLowerCase()}`
      );
    }

    // Add status filter to search queries
    if (listState.lastChosenStatus) {
      queryParams.document_status = listState.lastChosenStatus;
    } else if (
      listState.statusesFilter &&
      listState.statusesFilter.length > 0
    ) {
      queryParams.document_status = listState.statusesFilter;
    }

    const response: EntityListResponse<Entity> = yield call(
      listData,
      dataType,
      queryParams,
      "search",
      listState.pathParams
    );

    const relatedData = yield call(
      loadRelatedData,
      dataType,
      response.items,
      listState.relationships
    );

    // Ensures consistent pagination behavior across all list loading methods
    let totalCount;
    if (
      listState.lastChosenStatus &&
      response.status_counts &&
      response.status_counts[listState.lastChosenStatus]
    ) {
      // When filtering by status, use the count for that specific status
      totalCount = response.status_counts[listState.lastChosenStatus];
    } else if (response.total) {
      // If the API returns a total directly, use that
      totalCount = response.total;
    } else {
      // Otherwise calculate from all status counts
      totalCount = calculateTotalFromStatusCounts(response.status_counts);
    }

    yield put(
      listLoadSuccess(
        dataType,
        listId,
        ids(response.items),
        newPageIndex,
        relatedData,
        totalCount,
        response.limit,
        response.offset,
        response.status_counts
      )
    );

    return response;
  } catch (error) {
    yield put(listLoadFailure(dataType, listId, extractError(error)));
    return undefined;
  } finally {
    performanceMonitor.stop(metricId, { completed: true });
  }
}

/**
 * Main saga that decides between simple and search-based loading
 * based on presence of search/filter criteria
 */
export function* loadListDataSaga(
  action: PayloadAction<BaseListPayload>
): Generator<StrictEffect, void, any> {
  const { dataType, listId } = action.payload;
  const metricName = "Saga.loadListDataSaga";
  const initialContext = {
    sagaInstance: `${dataType}-${listId}-${action.type}`,
    ...action.payload
  };
  const metricId = performanceMonitor.start(metricName, initialContext);

  try {
    const listSelector = yield call(selectListState, dataType, listId);
    const listState: List | undefined = yield select(listSelector);
    if (!listState) {
      return;
    }

    const hasValidSearchOrFilterCriteria = isSearchOrFilterCriteriaValid(
      listState.searchCriteria,
      listState.filterCriteria
    );

    if (hasValidSearchOrFilterCriteria) {
      yield call(loadListDataWithServerSearch, dataType, listId, listState);
      return;
    }

    yield call(loadSimpleListData, dataType, listId, listState);
  } finally {
    performanceMonitor.stop(metricId);
  }
}

/**
 * Special saga for handling status filtering
 * Updates the lastChosenStatus in list state and triggers data reload
 */
export function* filterByStatusSaga(
  action: PayloadAction<{ listId: string; status: string; dataType: DataType }>
): Generator<StrictEffect, void, any> {
  const { listId: actionListId, status } = action.payload; // Renamed listId from payload to avoid clash
  const dataType = action.payload.dataType || DataType.Transaction; // As per original code context
  const metricName = "Saga.filterByStatusSaga";
  const initialContext = {
    sagaInstance: `${dataType}-${actionListId}-${status}`,
    ...action.payload,
    dataType
  };
  const metricId = performanceMonitor.start(metricName, initialContext);

  try {
    const listSelector = yield call(selectListState, dataType, actionListId);
    const listState: List | undefined = yield select(listSelector);

    if (!listState) {
      return;
    }

    const queryParams = {
      ...listState.queryParams
    };

    // Create updated list state with new status filter
    const updatedListState = {
      ...listState,
      queryParams,
      lastChosenStatus: status
    };

    // Load data with the status filter applied
    // Re-using loadSimpleListData as per original logic, could also be loadListDataWithServerSearch
    // depending on whether other search/filter criteria are active.
    // For simplicity and consistency with the original, calling loadSimpleListData.
    // If more complex logic is needed here, this might need to be revisited.
    yield call(
      loadSimpleListData,
      dataType, // Using the assumed DataType.Transaction
      actionListId,
      updatedListState
    );
  } finally {
    performanceMonitor.stop(metricId);
  }
}

// List of action types that trigger list loading functionality
const listLoadingActionTypes = [
  loadList.type,
  loadListWithoutClearing.type,
  loadNextListPage.type,
  loadSpecificListPage.type,
  refreshList.type,
  searchList.type,
  sortList.type,
  filterByStatus.type,
  updateQueryParams.type
];

/**
 * Root saga that manages all list loading requests
 * Handles cancellation of in-flight requests when new ones come in
 */
export default function* listSaga(): Generator<StrictEffect, void, any> {
  // Track running tasks to cancel them if new requests arrive
  const activeListLoadingTasks: Partial<Record<string, Task>> = {};
  while (true) {
    const nextListLoadAction = yield take(listLoadingActionTypes);

    // Special handling for status filtering
    if (nextListLoadAction.type === filterByStatus.type) {
      const taskKey = `${DataType.Transaction}-${nextListLoadAction.payload.listId}`;
      const runningListLoadingTask = activeListLoadingTasks[taskKey];
      if (runningListLoadingTask && runningListLoadingTask.isRunning()) {
        yield cancel(runningListLoadingTask);
      }
      activeListLoadingTasks[taskKey] = yield fork(
        filterByStatusSaga,
        nextListLoadAction
      );
    } else {
      // Handle all other list loading actions
      const taskKey = `${nextListLoadAction.payload.dataType}-${nextListLoadAction.payload.listId}`;
      const runningListLoadingTask = activeListLoadingTasks[taskKey];
      if (runningListLoadingTask && runningListLoadingTask.isRunning()) {
        yield cancel(runningListLoadingTask);
      }
      activeListLoadingTasks[taskKey] = yield fork(
        loadListDataSaga,
        nextListLoadAction
      );
    }
  }
}
